import React, { useMemo } from 'react';

import { HStack } from '@rtkwlf/fenrir-react';
import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useEnhancedScannerDetails } from '../../../../../../../../../../../hooks/use-enhanced-scanner-details/use-enhanced-scanner-details';
import type { CredentialedScanningRow } from '../../types';

import { CredentialedScanTarget } from './credentialed-scan-target/credentialed-scan-target';
import { CredentialedScanType } from './credentialed-scan-type/credentialed-scan-type';
import { DeleteCredentialButton } from './delete-credential-button/delete-credential-button';
import { EditCredentialButton } from './edit-credential-button/edit-credential-button';

const columnHelper = createColumnHelper<CredentialedScanningRow>();

export const useCredentialedScanningColumns = (): ColumnDef<
  CredentialedScanningRow,
  any
>[] => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { scannerId = '' } = useParams();
  const { data: { isConnected: isScannerConnected } = {} } =
    useEnhancedScannerDetails(scannerId);

  return useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: (row) => row.getValue(),
        enableSorting: true,
        header: t('credentialScan.name'),
      }),
      columnHelper.accessor('credScanningTypeStr', {
        cell: ({ row }) => (
          <CredentialedScanType scanningType={row.original.type} />
        ),
        enableSorting: true,
        header: t('credentialScan.type'),
      }),
      columnHelper.accessor('targetStr', {
        cell: ({ row }) => (
          <CredentialedScanTarget targets={row.original.targets} />
        ),
        header: t('credentialScan.targets'),
      }),
      columnHelper.accessor('description', {
        cell: (row) => row.getValue(),
        enableSorting: true,
        header: t('credentialScan.description'),
      }),
      columnHelper.accessor('id', {
        cell: ({ getValue, row }) => {
          return (
            <HStack gap="xsmall">
              <EditCredentialButton
                credentialedScanningRow={row.original}
                scannerId={scannerId}
                isDisabled={!isScannerConnected}
              />
              <DeleteCredentialButton
                credentialId={getValue()}
                scannerId={scannerId}
                isDisabled={!isScannerConnected}
              />
            </HStack>
          );
        },
        header: undefined,
        id: 'actions',
      }),
    ],
    [t, scannerId, isScannerConnected],
  );
};
